// components/exam/MCQFeedbackDashboard.tsx
import React, { useState } from 'react';
import MarkdownRenderer from '../common/MarkdownRenderer';
import '../../styles/components/MCQFeedbackDashboard.css';

interface MCQFeedbackDashboardProps {
  performance: {
    score: number;
    percentage: number;
    readinessScore: number;
    predictedOutcome: "Fail Risk" | "Borderline" | "Likely Pass";
  };
  weakTopics: Array<{
    topic: string;
    priority: 'HIGH' | 'MEDIUM';
    missedCount: number;
  }>;
  strongTopics: string[];
  actionPlan: Array<{
    step: number;
    topic: string;
    priority: 'HIGH' | 'MEDIUM';
    action: string;
  }>;
  performanceInsights: {
    recallStrength: number;
    speedEfficiency: number;
    confidenceAccuracyGap: number;
    blankRisk: number;
  };
  patternInsight?: string;
  questions: any[];
  answers: number[];
  theme: 'light' | 'dark';
  onTakeAnother: () => void;
  onSeeCorrectAnswers: () => void;
  onChat: () => void;
}

const MCQFeedbackDashboard: React.FC<MCQFeedbackDashboardProps> = ({
  performance,
  weakTopics,
  strongTopics,
  actionPlan,
  performanceInsights,
  patternInsight,
  questions,
  answers,
  theme,
  onTakeAnother,
  onSeeCorrectAnswers,
  onChat
}) => {
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const [showModelAnswers, setShowModelAnswers] = useState(false);

  const getReadinessColor = (score: number) => {
    if (score >= 70) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const getPredictedOutcomeIcon = (outcome: string) => {
    switch(outcome) {
      case 'Likely Pass': return '✅';
      case 'Borderline': return '⚠️';
      default: return '❌';
    }
  };

  // Format key insights from weak topics
  const getKeyInsights = () => {
    return weakTopics.slice(0, 4).map(topic => ({
      topic: topic.topic,
      issue: topic.priority === 'HIGH' ? 'Critical knowledge gap' : 'Needs reinforcement',
      mistake: `Missed ${topic.missedCount} question${topic.missedCount > 1 ? 's' : ''} in this area`,
      impact: topic.priority === 'HIGH' ? 'Major score impact' : 'Moderate impact'
    }));
  };

  const keyInsights = getKeyInsights();

  // Get performance issues
  const getPerformanceIssues = () => {
    const issues = [];
    if (performanceInsights.recallStrength < 60) issues.push('Recall under pressure');
    if (performanceInsights.speedEfficiency < 60) issues.push('Time management');
    if (performanceInsights.blankRisk > 30) issues.push('Unanswered questions');
    if (performanceInsights.confidenceAccuracyGap > 20) issues.push('Confidence vs accuracy mismatch');
    return issues;
  };

  const performanceIssues = getPerformanceIssues();

  // Calculate expected improvement
  const getExpectedImprovement = () => {
    let gain = 0;
    if (weakTopics.length > 0) {
      gain += weakTopics.filter(t => t.priority === 'HIGH').length * 8;
      gain += weakTopics.filter(t => t.priority === 'MEDIUM').length * 4;
    }
    if (performanceInsights.blankRisk > 30) gain += 5;
    if (performanceInsights.speedEfficiency < 60) gain += 5;
    
    gain = Math.min(30, Math.max(10, gain));
    return {
      current: performance.percentage,
      expected: Math.min(100, performance.percentage + gain),
      gain
    };
  };

  const improvement = getExpectedImprovement();

  return (
    <div className={`mcq-feedback-dashboard ${theme}`}>
      {/* 1. TOP SUMMARY CHARTS */}
      <div className="dashboard-header">
        <h2>📊 Exam Performance Summary</h2>
        <div className="score-summary-cards">
          <div className="score-card">
            <div className="score-label">Total Score</div>
            <div className="score-value">{performance.score}/{questions.length}</div>
            <div className="score-percentage">{performance.percentage}%</div>
            <div className="score-progress">
              <div className="progress-bar" style={{ width: `${performance.percentage}%`, backgroundColor: getReadinessColor(performance.percentage) }}></div>
            </div>
          </div>
          
          <div className="readiness-card">
            <div className="readiness-label">Readiness Score</div>
            <div className="readiness-value" style={{ color: getReadinessColor(performance.readinessScore) }}>
              {performance.readinessScore}%
            </div>
            <div className="readiness-progress">
              <div className="progress-bar" style={{ width: `${performance.readinessScore}%`, backgroundColor: getReadinessColor(performance.readinessScore) }}></div>
            </div>
            <div className="predicted-outcome">
              {getPredictedOutcomeIcon(performance.predictedOutcome)} {performance.predictedOutcome}
            </div>
          </div>
        </div>
      </div>

      {/* 2. KEY INSIGHTS - Where you lost marks */}
      {keyInsights.length > 0 && (
        <div className="key-insights-section">
          <div className="insights-header">
            <span className="insights-icon">🔍</span>
            <h3>Why You Lost Marks</h3>
          </div>
          <div className="insights-grid">
            {keyInsights.map((insight, idx) => (
              <div key={idx} className="insight-card">
                <div className="insight-topic">{insight.topic}</div>
                <div className="insight-details">
                  <div className="insight-row">
                    <span className="insight-label">Issue:</span>
                    <span className="insight-value">{insight.issue}</span>
                  </div>
                  <div className="insight-row">
                    <span className="insight-label">Mistake:</span>
                    <span className="insight-value">{insight.mistake}</span>
                  </div>
                  <div className="insight-impact">{insight.impact}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. WHAT TO FIX - Actionable steps */}
      {actionPlan.length > 0 && (
        <div className="action-highlights">
          <div className="action-header">
            <span className="action-icon">🎯</span>
            <h3>What to Fix</h3>
          </div>
          <div className="action-list">
            {actionPlan.slice(0, 4).map((action, idx) => (
              <div key={idx} className="action-item">
                <span className="action-number">{action.step}.</span>
                <span className="action-text">{action.action}</span>
                {action.priority === 'HIGH' && <span className="priority-badge">URGENT</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4. PATTERN INSIGHT */}
      {patternInsight && (
        <div className="pattern-insight">
          <div className="pattern-header">
            <span className="pattern-icon">🧠</span>
            <span className="pattern-badge">Pattern Detected</span>
          </div>
          <div className="pattern-content">
            <p>{patternInsight}</p>
            {performanceIssues.length > 0 && (
              <p className="pattern-impact">👉 This is why your score is at {performance.percentage}%</p>
            )}
          </div>
        </div>
      )}

      {/* 5. IMPROVEMENT PROJECTION */}
      <div className="improvement-projection">
        <div className="projection-header">
          <span className="projection-icon">📈</span>
          <span className="projection-title">If You Fix These Areas</span>
        </div>
        <div className="projection-values">
          <div className="current-score">
            <span className="label">Current</span>
            <span className="value">{improvement.current}%</span>
          </div>
          <div className="projection-arrow">→</div>
          <div className="target-score">
            <span className="label">Expected</span>
            <span className="value highlight">+{improvement.gain}%</span>
            <span className="value">{improvement.expected}%</span>
          </div>
        </div>
        <div className="projection-progress">
          <div className="progress-bar" style={{ width: `${improvement.current}%`, backgroundColor: getReadinessColor(improvement.current) }}></div>
          <div className="progress-target" style={{ left: `${improvement.expected}%` }}></div>
        </div>
      </div>

      {/* 6. RETEST CTA */}
      <div className={`retest-cta ${performance.readinessScore < 70 ? 'recommended' : 'completed'}`}>
        <div className="cta-content">
          <span className="cta-icon">{performance.readinessScore < 70 ? '🔄' : '🎉'}</span>
          <span className="cta-text">
            {performance.readinessScore < 70 ? 'Fix These Weak Areas Now' : 'Ready for the Real Exam!'}
          </span>
        </div>
        <button className="cta-button" onClick={onTakeAnother}>
          {performance.readinessScore < 70 ? 'Take Retest' : 'Practice More'}
        </button>
      </div>

      {/* 7. OPTIONAL DETAILS - Collapsible */}
      <div className="optional-details">
        <button 
          className={`collapse-toggle ${showDetailedBreakdown ? 'open' : ''}`}
          onClick={() => setShowDetailedBreakdown(!showDetailedBreakdown)}
        >
          <span className="toggle-icon">▼</span>
          <span className="toggle-text">View Full Marking Breakdown</span>
        </button>
        
        {showDetailedBreakdown && (
          <div className="detailed-breakdown">
            <div className="breakdown-header">
              <div className="col-topic">Topic</div>
              <div className="col-accuracy">Accuracy</div>
              <div className="col-status">Status</div>
            </div>
            {weakTopics.map((topic, idx) => (
              <div key={idx} className="breakdown-row">
                <div className="col-topic">{topic.topic}</div>
                <div className="col-accuracy">
                  <span className={`accuracy-badge ${topic.priority === 'HIGH' ? 'low' : 'medium'}`}>
                    {Math.round((topic.missedCount / (topic.missedCount + 1)) * 100)}%
                  </span>
                </div>
                <div className="col-status">
                  <span className={`status-badge ${topic.priority === 'HIGH' ? 'critical' : 'needs-work'}`}>
                    {topic.priority === 'HIGH' ? 'Critical' : 'Needs Work'}
                  </span>
                </div>
              </div>
            ))}
            {strongTopics.length > 0 && (
              <>
                <div className="breakdown-header strong">
                  <div className="col-topic">Strong Areas</div>
                  <div className="col-accuracy">Accuracy</div>
                  <div className="col-status">Status</div>
                </div>
                {strongTopics.slice(0, 3).map((topic, idx) => (
                  <div key={idx} className="breakdown-row strong">
                    <div className="col-topic">{topic}</div>
                    <div className="col-accuracy">
                      <span className="accuracy-badge high">85%+</span>
                    </div>
                    <div className="col-status">
                      <span className="status-badge strong">Strong</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        <button 
          className={`collapse-toggle ${showModelAnswers ? 'open' : ''}`}
          onClick={() => setShowModelAnswers(!showModelAnswers)}
        >
          <span className="toggle-icon">▼</span>
          <span className="toggle-text">View Model Answers</span>
        </button>
        
        {showModelAnswers && questions && questions.length > 0 && (
          <div className="model-answers">
            {questions.map((q, idx) => {
              const userAnswer = answers[idx];
              const isCorrect = userAnswer === q.correctAnswer;
              
              return (
                <div key={idx} className={`model-answer-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                  <div className="model-question">
                    <strong>Q{idx + 1}:</strong> <MarkdownRenderer content={q.question} theme={theme} />
                  </div>
                  <div className="answer-status-badge">
                    {isCorrect ? '✅ Correct' : '❌ Incorrect'}
                  </div>
                  <div className="model-answer-content">
                    <strong>Correct Answer:</strong>
                    <MarkdownRenderer content={q.options[q.correctAnswer]} theme={theme} />
                  </div>
                  <div className="explanation">
                    <strong>Explanation:</strong>
                    <MarkdownRenderer content={q.explanation} theme={theme} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="feedback-actions">
        <button className="action-btn see-answers" onClick={onSeeCorrectAnswers}>
          📖 See Correct Answers
        </button>
        <button className="action-btn take-another" onClick={onTakeAnother}>
          📝 Take Another Practice
        </button>
        <button className="action-btn chat" onClick={onChat}>
          💬 Ask AI Tutor
        </button>
      </div>
    </div>
  );
};

export default MCQFeedbackDashboard;